import { useFeatureSwitch, useCognitoUsername, useWorkspaceId } from "@skilitics/designer-auth/state";
import type { NodeTemplate } from "@skilitics/nautilus-endpoint-definition";

export type CanWriteNodeTemplateTarget = Pick<NodeTemplate, "scope" | "workspace" | "createdBy">;

export interface CanWriteNodeTemplateContext {
  isSuperUser: boolean;
  username: string | null;
  workspace: string | null;
}

export const canWriteNodeTemplate = (
  template: CanWriteNodeTemplateTarget,
  { isSuperUser, username, workspace }: CanWriteNodeTemplateContext,
): boolean => {
  if (isSuperUser) return true;
  switch (template.scope.type) {
    case "system":
      return false;
    case "user":
      // note: existing template createdBy values are Identity Pool identityIds not User Pool usernames.
      // if we keep this scope type we'll need to migrate (probably manually since it's a pain to map back,
      // which is why the change was made in the first place!)
      return template.createdBy === username;
    case "module":
    case "workspace":
      return template.workspace === workspace;
    default:
      console.warn("unknown template scope:", template.scope);
      return false;
  }
};

export const useCanWriteNodeTemplateContext = (): CanWriteNodeTemplateContext => {
  const isSuperUser = useFeatureSwitch("super-user");
  const username = useCognitoUsername();
  const workspace = useWorkspaceId();
  return { isSuperUser, username, workspace };
};

export const useCanWriteNodeTemplate = (template: CanWriteNodeTemplateTarget | null | undefined): boolean => {
  const context = useCanWriteNodeTemplateContext();
  return !!template && canWriteNodeTemplate(template, context);
};
